<template>
    <div class="integration-credentials">
        <load-screen-component :isLoading="isLoading"></load-screen-component>

        <div class="page-header-wrap">
            <page-header
                v-if="this.$hasPermission('IntegrationCredential_create')"
                :btnNew="'INTEGRATION_CREDENTIALS.NEW'"
                :pageTitle="'INTEGRATION_CREDENTIALS.INDEX_TITLE'"
                @newType="openCreateModal"
            />
            <page-header
                v-else
                :pageTitle="'INTEGRATION_CREDENTIALS.INDEX_TITLE'"
            />
        </div>

        <div class="filters-card">
            <div class="filters-header">
                <div class="filters-title">{{ t('INTEGRATION_CREDENTIALS.FILTERS') }}</div>
                <div class="filters-actions">
                    <button class="btn btn-custom bg-btn" @click="resetFilters">Limpar</button>
                    <button class="btn btn-primary" @click="index(1)">Aplicar</button>
                </div>
            </div>
            <div class="filters-grid">
                <div class="form-group">
                    <label>{{ t('INTEGRATION_CREDENTIALS.INTEGRATION') }}</label>
                    <select class="form-control" v-model="filters.integration_key">
                        <option value="">Todas</option>
                        <option v-for="integration in catalog.integrations" :key="integration.integration_key"
                                :value="integration.integration_key">
                            {{ integration.label }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label>{{ t('INTEGRATION_CREDENTIALS.ENVIRONMENT') }}</label>
                    <select class="form-control" v-model="filters.environment">
                        <option value="">Todos</option>
                        <option v-for="env in catalog.environments" :key="env" :value="env">
                            {{ environmentLabel(env) }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label>{{ t('INTEGRATION_CREDENTIALS.STATUS') }}</label>
                    <select class="form-control" v-model="filters.status">
                        <option value="">Todos</option>
                        <option value="active">{{ t('INTEGRATION_CREDENTIALS.STATUS_ACTIVE') }}</option>
                        <option value="inactive">{{ t('INTEGRATION_CREDENTIALS.STATUS_INACTIVE') }}</option>
                    </select>
                </div>
                <div class="form-group search">
                    <label>{{ t('INTEGRATION_CREDENTIALS.SEARCH') }}</label>
                    <div class="search-input">
                        <i class="icon-search1"/>
                        <input class="form-control" type="text" v-model="filters.search"
                               placeholder="Buscar por nome" @input="debouncedSearch"/>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isFetching" class="table-skeleton">
            <div class="skeleton-row" v-for="n in 6" :key="n">
                <div class="skeleton-cell" v-for="c in 7" :key="c"></div>
            </div>
        </div>

        <div v-else-if="items && items.data && items.data.length && !widthMd(575)" class="card table-card">
            <div class="table-responsive">
                <table class="table table-hover table-modern">
                    <thead>
                    <tr>
                        <th>{{ t('INTEGRATION_CREDENTIALS.INTEGRATION') }}</th>
                        <th>{{ t('INTEGRATION_CREDENTIALS.NAME') }}</th>
                        <th>{{ t('INTEGRATION_CREDENTIALS.ENVIRONMENT') }}</th>
                        <th>{{ t('INTEGRATION_CREDENTIALS.AUTH_TYPE') }}</th>
                        <th>{{ t('INTEGRATION_CREDENTIALS.STATUS') }}</th>
                        <th>{{ t('INTEGRATION_CREDENTIALS.LAST_VALIDATION') }}</th>
                        <th class="text-center">{{ t('INTEGRATION_CREDENTIALS.ACTIONS') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in items.data" :key="item.id">
                        <td>
                            <div class="integration-cell">
                                <div class="integration-icon"
                                     :class="{ 'integration-icon--image': isAppFacilita(item.integration_key) }">
                                    <img v-if="isAppFacilita(item.integration_key)"
                                         :src="facilitaIcon"
                                         class="integration-icon-img"
                                         alt="AppFacilita"/>
                                    <span v-else>{{ integrationInitials(item.integration_key) }}</span>
                                </div>
                                <div>
                                    <div class="integration-name">{{ integrationLabel(item.integration_key) }}</div>
                                    <div class="integration-key">{{ item.integration_key }}</div>
                                </div>
                            </div>
                        </td>
                        <td>{{ item.name }}</td>
                        <td>
                            <span class="badge badge-env">{{ environmentLabel(item.environment) }}</span>
                        </td>
                        <td>
                            <span class="badge badge-auth">{{ item.auth_type }}</span>
                        </td>
                        <td>
                            <span class="badge" :class="item.status === 'active' ? 'badge-success' : 'badge-muted'">
                                {{ item.status === 'active' ? t('INTEGRATION_CREDENTIALS.STATUS_ACTIVE') : t('INTEGRATION_CREDENTIALS.STATUS_INACTIVE') }}
                            </span>
                        </td>
                        <td>
                            <div v-if="item.last_validated_at" class="validation-info">
                                <div>{{ formatDate(item.last_validated_at) }}</div>
                                <small v-if="item.last_validation_result?.status"
                                       :class="item.last_validation_result?.status === 'success' ? 'text-success' : 'text-danger'">
                                    {{ item.last_validation_result?.status }}
                                </small>
                            </div>
                            <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-center">
                            <div class="text-right">
                                <a type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
                                   class="w-100" style="font-size: 20px" id="listDropdown">
                                    <i class="icon-more_vert" title="Opções"></i>
                                </a>
                                <div class="dropdown-menu dropdown-menu-right dropdown-black w-auto">
                                    <div class="dropdown-item pointer text-white"
                                         v-if="this.$hasPermission('IntegrationCredential_update')"
                                         data-toggle="modal" data-target="#customModalTwo"
                                         @click="openEditModal(item)">
                                        <i class="icon-edit mr-2 font-15"/>Editar
                                    </div>
                                    <div class="dropdown-item pointer text-white"
                                         v-if="this.$hasPermission('IntegrationCredential_validate')"
                                         :class="validatingId === item.id ? 'disabled' : ''"
                                         @click="validatingId === item.id ? null : validateCredential(item)">
                                        <i class="icon-check-circle mr-2 font-15"/>
                                        {{ validatingId === item.id ? t('INTEGRATION_CREDENTIALS.VALIDATING') : t('INTEGRATION_CREDENTIALS.VALIDATE') }}
                                    </div>
                                    <div class="dropdown-item pointer text-white"
                                         v-if="this.$hasPermission('IntegrationCredential_update')"
                                         @click="toggleStatus(item)">
                                        <i class="icon-toggle-right mr-2 font-15"/>
                                        {{ item.status === 'active'
                                            ? t('INTEGRATION_CREDENTIALS.DEACTIVATE')
                                            : t('INTEGRATION_CREDENTIALS.ACTIVATE') }}
                                    </div>
                                    <div class="dropdown-item pointer text-white"
                                         v-if="this.$hasPermission('IntegrationCredential_delete')"
                                         @click="showAlert(item.id)">
                                        <i class="icon-delete mr-2 font-15"/>Excluir
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-else-if="items && items.data && items.data.length && widthMd(575)">
            <div class="card integration-card" v-for="item in items.data" :key="item.id">
                <div class="card-body">
                    <div class="card-header-row">
                        <div class="integration-card-header">
                            <div class="integration-icon"
                                 :class="{ 'integration-icon--image': isAppFacilita(item.integration_key) }">
                                <img v-if="isAppFacilita(item.integration_key)"
                                     :src="facilitaIcon"
                                     class="integration-icon-img"
                                     alt="AppFacilita"/>
                                <span v-else>{{ integrationInitials(item.integration_key) }}</span>
                            </div>
                            <div class="integration-name">{{ integrationLabel(item.integration_key) }}</div>
                            <div class="integration-key">{{ item.integration_key }}</div>
                        </div>
                        <div class="text-right">
                            <a type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
                               class="w-100" style="font-size: 20px" id="listDropdown">
                                <i class="icon-more_vert" title="Opções"></i>
                            </a>
                            <div class="dropdown-menu dropdown-menu-right dropdown-black w-auto">
                                <div class="dropdown-item pointer text-white"
                                     v-if="this.$hasPermission('IntegrationCredential_update')"
                                     data-toggle="modal" data-target="#customModalTwo"
                                     @click="openEditModal(item)">
                                    <i class="icon-edit mr-2 font-15"/>Editar
                                </div>
                                <div class="dropdown-item pointer text-white"
                                     v-if="this.$hasPermission('IntegrationCredential_validate')"
                                     :class="validatingId === item.id ? 'disabled' : ''"
                                     @click="validatingId === item.id ? null : validateCredential(item)">
                                    <i class="icon-check-circle mr-2 font-15"/>
                                    {{ validatingId === item.id ? t('INTEGRATION_CREDENTIALS.VALIDATING') : t('INTEGRATION_CREDENTIALS.VALIDATE') }}
                                </div>
                                <div class="dropdown-item pointer text-white"
                                     v-if="this.$hasPermission('IntegrationCredential_update')"
                                     @click="toggleStatus(item)">
                                    <i class="icon-toggle-right mr-2 font-15"/>
                                    {{ item.status === 'active'
                                        ? t('INTEGRATION_CREDENTIALS.DEACTIVATE')
                                        : t('INTEGRATION_CREDENTIALS.ACTIVATE') }}
                                </div>
                                <div class="dropdown-item pointer text-white"
                                     v-if="this.$hasPermission('IntegrationCredential_delete')"
                                     @click="showAlert(item.id)">
                                    <i class="icon-delete mr-2 font-15"/>Excluir
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card-grid">
                        <div class="card-field">
                            <label>{{ t('INTEGRATION_CREDENTIALS.NAME') }}</label>
                            <div>{{ item.name }}</div>
                        </div>
                        <div class="card-field">
                            <label>{{ t('INTEGRATION_CREDENTIALS.ENVIRONMENT') }}</label>
                            <div><span class="badge badge-env">{{ environmentLabel(item.environment) }}</span></div>
                        </div>
                        <div class="card-field">
                            <label>{{ t('INTEGRATION_CREDENTIALS.AUTH_TYPE') }}</label>
                            <div><span class="badge badge-auth">{{ item.auth_type }}</span></div>
                        </div>
                        <div class="card-field">
                            <label>{{ t('INTEGRATION_CREDENTIALS.STATUS') }}</label>
                            <div>
                                <span class="badge" :class="item.status === 'active' ? 'badge-success' : 'badge-muted'">
                                    {{ item.status === 'active' ? t('INTEGRATION_CREDENTIALS.STATUS_ACTIVE') : t('INTEGRATION_CREDENTIALS.STATUS_INACTIVE') }}
                                </span>
                            </div>
                        </div>
                        <div class="card-field">
                            <label>{{ t('INTEGRATION_CREDENTIALS.LAST_VALIDATION') }}</label>
                            <div v-if="item.last_validated_at" class="validation-info">
                                <div>{{ formatDate(item.last_validated_at) }}</div>
                                <small v-if="item.last_validation_result?.status"
                                       :class="item.last_validation_result?.status === 'success' ? 'text-success' : 'text-danger'">
                                    {{ item.last_validation_result?.status }}
                                </small>
                            </div>
                            <span v-else class="text-muted">-</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="empty-state">
            <div class="empty-card">
                <div class="empty-icon"><i class="icon-lock-open"/></div>
                <h4>{{ t('INTEGRATION_CREDENTIALS.EMPTY_TITLE') }}</h4>
                <p>{{ t('INTEGRATION_CREDENTIALS.EMPTY_SUBTITLE') }}</p>
                <button v-if="this.$hasPermission('IntegrationCredential_create')" class="btn btn-primary" data-toggle="modal"
                        data-target="#customModalTwo" @click="openCreateModal">
                    {{ t('INTEGRATION_CREDENTIALS.NEW') }}
                </button>
            </div>
        </div>

        <pagination-component
            v-if="items && items.data && items.data.length"
            :items="items"
            :to="'/credenciais-integracoes'"
            @changePage="index($event)"
        />

        <modal :titleModal="modalTitle" :modalLarge="true">
            <template v-slot:title>{{ t(modalTitle) }}</template>
            <form @submit.prevent="submitForm">
                <div class="modal-body">
                    <div class="modal-section">
                        <h6>{{ t('INTEGRATION_CREDENTIALS.FORM') }}</h6>
                        <p class="text-muted">{{ t('INTEGRATION_CREDENTIALS.FORM_HINT') }}</p>
                    </div>

                    <div class="form-grid">
                        <div class="form-group">
                            <label class="required">{{ t('INTEGRATION_CREDENTIALS.INTEGRATION') }}</label>
                            <select class="form-control" v-model="form.integration_key" @change="onIntegrationChange" :disabled="isEditing">
                                <option value="" disabled>Selecione</option>
                                <option v-for="integration in catalog.integrations" :key="integration.integration_key"
                                        :value="integration.integration_key">
                                    {{ integration.label }}
                                </option>
                            </select>
                            <div class="validation" v-if="formErrors.integration_key">{{ formErrors.integration_key }}</div>
                        </div>
                        <div class="form-group">
                            <label class="required">{{ t('INTEGRATION_CREDENTIALS.NAME') }}</label>
                            <input class="form-control" v-model="form.name" type="text" placeholder="Ex: AppFacilita Produção"/>
                            <div class="validation" v-if="formErrors.name">{{ formErrors.name }}</div>
                        </div>
                        <div class="form-group">
                            <label class="required">{{ t('INTEGRATION_CREDENTIALS.ENVIRONMENT') }}</label>
                            <select class="form-control" v-model="form.environment">
                                <option value="" disabled>Selecione</option>
                                <option v-for="env in catalog.environments" :key="env" :value="env">
                                    {{ environmentLabel(env) }}
                                </option>
                            </select>
                            <div class="validation" v-if="formErrors.environment">{{ formErrors.environment }}</div>
                        </div>
                        <div class="form-group">
                            <label class="required">{{ t('INTEGRATION_CREDENTIALS.AUTH_TYPE') }}</label>
                            <select class="form-control" v-model="form.auth_type">
                                <option value="" disabled>Selecione</option>
                                <option v-for="auth in authTypesForSelected" :key="auth" :value="auth">{{ auth }}</option>
                            </select>
                            <div class="validation" v-if="formErrors.auth_type">{{ formErrors.auth_type }}</div>
                        </div>
                        <div class="form-group">
                            <label>{{ t('INTEGRATION_CREDENTIALS.STATUS') }}</label>
                            <select class="form-control" v-model="form.status">
                                <option value="active">{{ t('INTEGRATION_CREDENTIALS.STATUS_ACTIVE') }}</option>
                                <option value="inactive">{{ t('INTEGRATION_CREDENTIALS.STATUS_INACTIVE') }}</option>
                            </select>
                        </div>
                    </div>

                    <div v-if="selectedIntegrationFields.length" class="dynamic-fields">
                        <div class="field-card" v-for="field in selectedIntegrationFields" :key="field.key">
                            <label :class="field.required ? 'required' : ''">{{ field.label }}</label>

                            <input v-if="field.type === 'text' && isAppFacilitaBaseUrlLocked(field)" class="form-control"
                                   :placeholder="field.placeholder || ''"
                                   :value="getAppFacilitaBaseUrl()"
                                   disabled type="text"/>

                            <input v-else-if="field.type === 'text'" class="form-control"
                                   :placeholder="field.placeholder || ''"
                                   v-model="form.credentials[field.key]" type="text"/>

                            <textarea v-else-if="field.type === 'textarea'" class="form-control"
                                      :placeholder="field.placeholder || ''"
                                      v-model="form.credentials[field.key]" rows="3"></textarea>

                            <select v-else-if="field.type === 'select'" class="form-control"
                                    v-model="form.credentials[field.key]">
                                <option value="" disabled>Selecione</option>
                                <option v-for="option in field.options || []" :key="option.value || option"
                                        :value="option.value || option">
                                    {{ option.label || option }}
                                </option>
                            </select>

                            <div v-else-if="field.type === 'toggle'" class="custom-control custom-switch">
                                <input type="checkbox" class="custom-control-input"
                                       :id="`toggle-${field.key}`" v-model="form.credentials[field.key]"/>
                                <label class="custom-control-label" :for="`toggle-${field.key}`"></label>
                            </div>

                            <input v-else class="form-control" type="password"
                                   :placeholder="secretPlaceholder(field.key)"
                                   v-model="form.credentials[field.key]"/>

                            <small v-if="field.type === 'password' && isEditing" class="text-muted">
                                {{ t('INTEGRATION_CREDENTIALS.SECRET_HINT') }}
                            </small>
                            <small v-if="field.help" class="text-muted">{{ field.help }}</small>
                            <div class="validation" v-if="formErrors[`credentials.${field.key}`]">
                                {{ formErrors[`credentials.${field.key}`] }}
                            </div>
                        </div>
                    </div>

                    <div class="extra-fields">
                        <div class="extra-fields-header">
                            <h6>{{ t('INTEGRATION_CREDENTIALS.EXTRA_FIELDS') }}</h6>
                            <button class="btn btn-outline-primary btn-sm" type="button" @click="addExtraField">
                                + {{ t('INTEGRATION_CREDENTIALS.ADD_FIELD') }}
                            </button>
                        </div>
                        <p class="text-muted">{{ t('INTEGRATION_CREDENTIALS.EXTRA_FIELDS_HINT') }}</p>
                        <div class="extra-fields-grid" v-if="extraFields.length">
                            <div class="field-card" v-for="(field, index) in extraFields" :key="field.id">
                                <div class="extra-field-row">
                                    <input class="form-control" type="text" v-model="field.key"
                                           placeholder="Chave (ex: client_id)"/>
                                    <select class="form-control" v-model="field.type">
                                        <option value="text">Texto</option>
                                        <option value="password">Senha</option>
                                        <option value="textarea">Texto longo</option>
                                    </select>
                                    <button class="btn btn-outline-danger btn-sm" type="button"
                                            @click="removeExtraField(index)">
                                        {{ t('ACTIONS.DELETE') }}
                                    </button>
                                </div>
                                <input v-if="field.type === 'text'" class="form-control mt-2" type="text"
                                       v-model="field.value" :placeholder="field.placeholder || 'Valor'"/>
                                <input v-else-if="field.type === 'password'" class="form-control mt-2" type="password"
                                       v-model="field.value" :placeholder="field.placeholder || 'Valor'"/>
                                <textarea v-else class="form-control mt-2" rows="3"
                                          v-model="field.value" :placeholder="field.placeholder || 'Valor'"></textarea>
                                <small v-if="field.type === 'password' && isEditing" class="text-muted">
                                    {{ t('INTEGRATION_CREDENTIALS.SECRET_HINT') }}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer2 d-flex align-items-center">
                    <div class="justify-content-left">
                        <h5 class="ml-2" style="color: red; font-size: .7rem;">{{ t('GENERAL.REQUIRED_FIELDS') }} *</h5>
                    </div>
                    <div class="ml-auto d-flex align-items-center">
                        <button class="btn btn-secondary" data-dismiss="modal" type="button" id="closeX">
                            {{ t('ACTIONS.CLOSE') }}
                        </button>
                        <button class="btn btn-primary ml-2" type="submit">
                            {{ t('ACTIONS.SAVE') }}
                        </button>
                    </div>
                </div>
            </form>
        </modal>
    </div>
</template>

<script>
import {useI18n} from 'vue-i18n';
import {defineComponent} from 'vue';
import {useToast} from 'vue-toastification';
import Modal from '@/components/modal';
import Permission from '@/components/permission.vue';
import Validate from '@/components/validate.vue';
import pageHeader from '@/components/layouts/pageHeader';
import PaginationComponent from '@/components/layouts/PaginationComponent';
import loadScreenComponent from '@/components/layouts/loadScreenComponent';
import integrationCredentials from '@/services/integrationCredentials';
import {mapState} from 'vuex';
import facilitaIcon from '@/assets/img/facilita.ico';
import widthMd from '@/mixins/widthMd.vue';

export default defineComponent({
    name: 'indexIntegrationCredentials',
    mixins: [Permission, Validate, widthMd],
    components: {
        Modal,
        pageHeader,
        PaginationComponent,
        loadScreenComponent,
    },
    computed: {
        ...mapState({
            isLoading: 'isLoading',
        }),
        authTypesForSelected() {
            return this.selectedIntegration?.auth_types || this.catalog.auth_types || [];
        },
    },
    setup() {
        const toast = useToast();
        const {t} = useI18n();
        return {t, toast, facilitaIcon};
    },
    data() {
        return {
            items: null,
            isFetching: false,
            modalTitle: 'INTEGRATION_CREDENTIALS.NEW',
            catalog: {
                integrations: [],
                environments: [],
                auth_types: [],
            },
            filters: {
                integration_key: '',
                environment: '',
                status: '',
                search: '',
            },
            searchTimeout: null,
            selectedIntegration: null,
            selectedIntegrationFields: [],
            extraFields: [],
            form: {
                id: null,
                integration_key: '',
                name: '',
                environment: 'production',
                auth_type: '',
                status: 'active',
                credentials: {},
            },
            formErrors: {},
            isEditing: false,
            secretHints: {},
            validatingId: null,
            currentHasSecret: false,
            appFacilitaBaseUrl: process.env.VUE_APP_APP_FACILITA_BASE_URL
                || 'https://api.facilitaapp.com',
        };
    },
    mounted() {
        this.$store.commit('changeLoading', true);
        this.loadCatalog().then(() => this.index());
    },
    watch: {
        'form.environment'() {
            this.applyAppFacilitaBaseUrl();
        },
        'form.integration_key'() {
            this.applyAppFacilitaBaseUrl();
        },
    },
    methods: {
        async loadCatalog() {
            try {
                const resp = await integrationCredentials.catalog();
                this.catalog = resp.data;
            } catch (error) {
                this.errorMsg(error);
            } finally {
                this.$store.commit('changeLoading', false);
            }
        },
        async index(page = 1) {
            this.isFetching = true;
            const params = {
                page,
                ...this.filters,
            };
            try {
                const resp = await integrationCredentials.index(params);
                this.items = resp.data;
            } catch (error) {
                this.errorMsg(error);
            } finally {
                this.isFetching = false;
                this.$store.commit('changeLoading', false);
            }
        },
        debouncedSearch() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }
            this.searchTimeout = setTimeout(() => this.index(1), 400);
        },
        resetFilters() {
            this.filters = {
                integration_key: '',
                environment: '',
                status: '',
                search: '',
            };
            this.index(1);
        },
        integrationLabel(key) {
            const found = this.catalog.integrations.find((item) => item.integration_key === key);
            return found ? found.label : key;
        },
        integrationInitials(key) {
            const label = this.integrationLabel(key) || '';
            return label.split(' ').map(word => word[0]).join('').slice(0, 2).toUpperCase();
        },
        isAppFacilita(key) {
            return String(key || '').toLowerCase() === 'appfacilita';
        },
        environmentLabel(env) {
            if (env === 'production') return this.t('INTEGRATION_CREDENTIALS.ENV_PRODUCTION');
            if (env === 'sandbox') return this.t('INTEGRATION_CREDENTIALS.ENV_SANDBOX');
            return env;
        },
        formatDate(value) {
            if (!value) return '-';
            return new Date(value).toLocaleString('pt-BR');
        },
        openCreateModal() {
            this.isEditing = false;
            this.modalTitle = 'INTEGRATION_CREDENTIALS.NEW';
            this.form = {
                id: null,
                integration_key: '',
                name: '',
                environment: 'production',
                auth_type: '',
                status: 'active',
                credentials: {},
            };
            this.selectedIntegration = null;
            this.selectedIntegrationFields = [];
            this.secretHints = {};
            this.formErrors = {};
            this.currentHasSecret = false;
            this.extraFields = [];
        },
        async openEditModal(item) {
            this.isEditing = true;
            this.modalTitle = 'INTEGRATION_CREDENTIALS.EDIT';
            this.formErrors = {};
            this.secretHints = {};

            try {
                const resp = await integrationCredentials.show(item.id);
                const data = resp.data;
                this.form = {
                    id: data.id,
                    integration_key: data.integration_key,
                    name: data.name,
                    environment: data.environment,
                    auth_type: data.auth_type,
                    status: data.status,
                    credentials: {},
                };
                this.currentHasSecret = !!data.has_secret;
                this.onIntegrationChange();
                this.fillCredentialsFromData(data);
                this.buildExtraFieldsFromData(data);
                this.applyAppFacilitaBaseUrl();
            } catch (error) {
                this.errorMsg(error);
            }
        },
        onIntegrationChange() {
            this.selectedIntegration = this.catalog.integrations.find(
                (item) => item.integration_key === this.form.integration_key
            );
            this.selectedIntegrationFields = this.selectedIntegration?.fields || [];
            if (!this.form.auth_type && this.selectedIntegration?.auth_types?.length) {
                this.form.auth_type = this.selectedIntegration.auth_types[0];
            }
            this.initializeCredentials();
            this.applyAppFacilitaBaseUrl();
        },
        initializeCredentials() {
            const credentials = {};
            this.selectedIntegrationFields.forEach((field) => {
                if (field.type === 'toggle') {
                    credentials[field.key] = false;
                } else {
                    credentials[field.key] = '';
                }
            });
            this.form.credentials = credentials;
            this.applyAppFacilitaBaseUrl();
        },
        isAppFacilitaProduction() {
            return this.isAppFacilita(this.form.integration_key) && this.form.environment === 'production';
        },
        getAppFacilitaBaseUrl() {
            return String(this.appFacilitaBaseUrl || '').trim();
        },
        applyAppFacilitaBaseUrl() {
            if (!this.isAppFacilitaProduction()) return;
            const baseUrl = this.getAppFacilitaBaseUrl();
            if (!this.form.credentials) {
                this.form.credentials = {};
            }
            this.form.credentials = {
                ...this.form.credentials,
                // Always reset and enforce env base URL when production is selected.
                base_url: baseUrl || '',
            };
        },
        isAppFacilitaBaseUrlLocked(field) {
            return this.isAppFacilitaProduction() && field.key === 'base_url';
        },
        buildExtraFieldsFromData(data) {
            const publicData = data.credentials_public || {};
            const maskedData = data.credentials_masked || {};
            const schemaKeys = new Set(this.selectedIntegrationFields.map((field) => field.key));
            const extraKeys = new Set([
                ...Object.keys(publicData || {}),
                ...Object.keys(maskedData || {}),
            ]);

            const extraFields = [];
            extraKeys.forEach((key) => {
                if (schemaKeys.has(key)) return;
                const hasPublic = Object.prototype.hasOwnProperty.call(publicData, key);
                const hasMasked = Object.prototype.hasOwnProperty.call(maskedData, key);
                const isSecret = hasMasked && !hasPublic;
                extraFields.push({
                    id: `${key}-${Date.now()}`,
                    key,
                    value: hasPublic ? publicData[key] : '',
                    type: isSecret ? 'password' : 'text',
                    placeholder: isSecret ? (maskedData[key] || '********') : 'Valor',
                });
            });
            this.extraFields = extraFields;
        },
        fillCredentialsFromData(data) {
            const publicData = data.credentials_public || {};
            const maskedData = data.credentials_masked || {};
            const credentials = {...this.form.credentials};

            this.selectedIntegrationFields.forEach((field) => {
                if (field.type === 'password') {
                    credentials[field.key] = '';
                    this.secretHints[field.key] = maskedData[field.key] || '********';
                } else if (publicData[field.key] !== undefined) {
                    credentials[field.key] = publicData[field.key];
                } else if (maskedData[field.key] !== undefined) {
                    credentials[field.key] = maskedData[field.key];
                }
            });

            this.form.credentials = credentials;
        },
        addExtraField() {
            this.extraFields.push({
                id: `${Date.now()}-${Math.random()}`,
                key: '',
                value: '',
                type: 'text',
            });
        },
        removeExtraField(index) {
            this.extraFields.splice(index, 1);
        },
        secretPlaceholder(key) {
            return this.secretHints[key] || '********';
        },
        validateForm() {
            const missingBaseUrl = this.ensureAppFacilitaBaseUrl();
            const errors = {};
            if (!this.form.integration_key) {
                errors.integration_key = this.t('INTEGRATION_CREDENTIALS.FIELD_REQUIRED');
            }
            if (!this.form.name) {
                errors.name = this.t('INTEGRATION_CREDENTIALS.FIELD_REQUIRED');
            }
            if (!this.form.environment) {
                errors.environment = this.t('INTEGRATION_CREDENTIALS.FIELD_REQUIRED');
            }
            if (!this.form.auth_type) {
                errors.auth_type = this.t('INTEGRATION_CREDENTIALS.FIELD_REQUIRED');
            }
            this.selectedIntegrationFields.forEach((field) => {
                if (!field.required) return;
                let value = this.form.credentials[field.key];
                if (field.key === 'base_url' && this.isAppFacilitaProduction()) {
                    const envBaseUrl = this.getAppFacilitaBaseUrl();
                    if (envBaseUrl) {
                        value = envBaseUrl;
                        this.form.credentials = {
                            ...this.form.credentials,
                            base_url: envBaseUrl,
                        };
                    }
                }
                const isSecret = field.type === 'password';
                if (isSecret && this.isEditing && !value && this.currentHasSecret) {
                    return;
                }
                if (value === null || value === undefined || value === '') {
                    errors[`credentials.${field.key}`] = this.t('INTEGRATION_CREDENTIALS.FIELD_REQUIRED');
                }
            });
            if (missingBaseUrl) {
                errors['credentials.base_url'] = this.t('INTEGRATION_CREDENTIALS.FIELD_REQUIRED');
            }
            this.formErrors = errors;
            return Object.keys(errors).length === 0;
        },
        ensureAppFacilitaBaseUrl() {
            this.applyAppFacilitaBaseUrl();
            if (!this.isAppFacilitaProduction()) return;
            const baseUrl = this.getAppFacilitaBaseUrl();
            if (baseUrl) return false;
            if (!this.form.credentials) {
                this.form.credentials = {};
            }
            return true;
        },
        buildCredentialsPayload() {
            const payload = {};
            this.selectedIntegrationFields.forEach((field) => {
                const value = this.form.credentials[field.key];
                if (field.type === 'password') {
                    if (value !== null && value !== undefined && value !== '') {
                        payload[field.key] = value;
                    }
                    return;
                }
                if (field.type === 'toggle') {
                    payload[field.key] = !!value;
                    return;
                }
                if (value !== null && value !== undefined && value !== '') {
                    payload[field.key] = value;
                }
            });
            if (this.isAppFacilitaProduction()) {
                const envBaseUrl = this.getAppFacilitaBaseUrl();
                if (envBaseUrl) {
                    payload.base_url = envBaseUrl;
                }
            }
            this.extraFields.forEach((field) => {
                if (!field.key) return;
                if (field.value === null || field.value === undefined || field.value === '') return;
                payload[field.key] = field.value;
            });
            return payload;
        },
        async validateBeforeSave(payload) {
            try {
                const resp = await integrationCredentials.validatePayload(payload);
                const status = resp.data?.status;
                const message = resp.data?.message;
                if (status === 'success') {
                    return {ok: true, message};
                }
                return {ok: false, message: message || this.t('INTEGRATION_CREDENTIALS.VALIDATION_ERROR')};
            } catch (error) {
                const apiMessage = error?.response?.data?.validations?.message;
                const apiErrors = error?.response?.data?.validations?.errors;
                if (apiErrors) {
                    this.formErrors = Object.keys(apiErrors).reduce((acc, key) => {
                        const fieldKey = key.startsWith('credentials.') ? key : `credentials.${key}`;
                        acc[fieldKey] = Array.isArray(apiErrors[key]) ? apiErrors[key][0] : apiErrors[key];
                        return acc;
                    }, {});
                }
                return {ok: false, message: apiMessage || this.t('INTEGRATION_CREDENTIALS.VALIDATION_ERROR')};
            }
        },
        async submitForm() {
            if (!this.validateForm()) return;

            const payload = {
                id: this.form.id,
                integration_key: this.form.integration_key,
                name: this.form.name,
                environment: this.form.environment,
                auth_type: this.form.auth_type,
                status: this.form.status,
            };

            const credentialsPayload = this.buildCredentialsPayload();
            if (Object.keys(credentialsPayload).length) {
                payload.credentials = credentialsPayload;
            }

            this.$store.commit('changeLoading', true);
            try {
                const validation = await this.validateBeforeSave({
                    id: this.isEditing ? this.form.id : undefined,
                    integration_key: payload.integration_key,
                    environment: payload.environment,
                    auth_type: payload.auth_type,
                    credentials: payload.credentials || {},
                });
                if (!validation.ok) {
                    this.toast.error(validation.message);
                    return;
                }
                if (this.isEditing) {
                    await integrationCredentials.update(payload);
                } else {
                    await integrationCredentials.create(payload);
                }
                document.getElementById('closeX').click();
                await this.index();
                this.toast.success(this.t('INTEGRATION_CREDENTIALS.SAVED_SUCCESS'));
            } catch (error) {
                this.errorMsg(error);
            } finally {
                this.$store.commit('changeLoading', false);
            }
        },
        showAlert(id) {
            this.$swal.fire({
                title: this.t('INTEGRATION_CREDENTIALS.DELETE_CONFIRM'),
                showCancelButton: true,
                showCloseButton: true,
                confirmButtonText: 'Excluir',
                cancelButtonText: 'Cancelar',
            }).then((result) => {
                if (result.isConfirmed) {
                    this.$store.commit('changeLoading', true);
                    integrationCredentials.delete(id).then(() => {
                        this.index();
                        this.toast.success(this.t('INTEGRATION_CREDENTIALS.DELETED_SUCCESS'));
                    }).catch((error) => {
                        this.errorMsg(error);
                    }).finally(() => {
                        this.$store.commit('changeLoading', false);
                    });
                }
            });
        },
        async validateCredential(item) {
            if (this.validatingId) return;
            this.validatingId = item.id;
            try {
                const resp = await integrationCredentials.validate(item.id);
                const status = resp.data?.status;
                const message = resp.data?.message;
                if (status === 'success') {
                    this.toast.success(message || this.t('INTEGRATION_CREDENTIALS.VALIDATION_SUCCESS'));
                } else {
                    this.toast.info(message || this.t('INTEGRATION_CREDENTIALS.VALIDATION_ERROR'));
                }
                await this.index(this.items?.current_page || 1);
            } catch (error) {
                this.errorMsg(error);
            } finally {
                this.validatingId = null;
            }
        },
        async toggleStatus(item) {
            const nextStatus = item.status === 'active' ? 'inactive' : 'active';
            try {
                await integrationCredentials.update({
                    id: item.id,
                    status: nextStatus,
                });
                await this.index(this.items?.current_page || 1);
                const messageKey = nextStatus === 'active'
                    ? 'INTEGRATION_CREDENTIALS.ACTIVATED_SUCCESS'
                    : 'INTEGRATION_CREDENTIALS.DEACTIVATED_SUCCESS';
                this.toast.success(this.t(messageKey));
            } catch (error) {
                this.errorMsg(error);
            }
        },
    },
});
</script>

<style scoped>
.integration-credentials {
    padding-bottom: 2rem;
}

.page-header-wrap {
    max-width: 100%;
    overflow-x: visible;
}

.filters-card {
    background: linear-gradient(135deg, #ffffff, #f7f8fb);
    border: 1px solid #e5e7ef;
    border-radius: 14px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.filters-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
}

.filters-title {
    font-weight: 600;
    font-size: 1rem;
    color: #1f2937;
}

.filters-actions .btn + .btn {
    margin-left: 0.5rem;
}

.filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
}

.search-input {
    position: relative;
}

.search-input i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9aa4b2;
}

.search-input input {
    padding-left: 34px;
}

.table-card {
    border-radius: 16px;
    overflow: hidden;
    border: none;
    box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
}

.table-modern th {
    background: #f6f7fb;
    color: #1f2937;
    font-weight: 600;
    border-top: none;
}

.table-modern td {
    vertical-align: middle;
}

.integration-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.integration-icon {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: #111827;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.integration-icon--image {
    background: #ffffff;
    border: 1px solid #e5e7eb;
}

.integration-icon-img {
    width: 22px;
    height: 22px;
    object-fit: contain;
}

.integration-name {
    font-weight: 600;
}

.integration-key {
    font-size: 0.75rem;
    color: #6b7280;
}

.badge-env {
    background: #e0f2fe;
    color: #075985;
    padding: 0.35rem 0.6rem;
    border-radius: 10px;
}

.badge-auth {
    background: #ede9fe;
    color: #5b21b6;
    padding: 0.35rem 0.6rem;
    border-radius: 10px;
}

.badge-muted {
    background: #e5e7eb;
    color: #6b7280;
}

.table-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: center;
}

.integration-card {
    border-radius: 16px;
    border: none;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.1);
    margin-bottom: 1rem;
}

.card-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding-bottom: 0.75rem;
    margin-bottom: 0.75rem;
    border-bottom: 1px solid #eef2f7;
}

.integration-card-header {
    display: grid;
    grid-template-columns: 42px 1fr;
    gap: 0.75rem;
    align-items: center;
}

.card-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
}

.card-field label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    margin-bottom: 0.2rem;
}

.card-field div {
    font-weight: 600;
    color: #111827;
}

.validation-info small {
    display: block;
}

.empty-state {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
}

.empty-card {
    text-align: center;
    padding: 2.5rem;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
}

.empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
}

.table-skeleton {
    border-radius: 16px;
    background: #fff;
    padding: 1.5rem;
    box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
}

.skeleton-row {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0.75rem;
    margin-bottom: 0.75rem;
}

.skeleton-cell {
    height: 18px;
    border-radius: 10px;
    background: linear-gradient(90deg, #f0f2f7, #e5e7ef, #f0f2f7);
    background-size: 200% 100%;
    animation: shimmer 1.6s infinite;
}

.modal-section h6 {
    font-weight: 600;
    margin-bottom: 0.35rem;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
}

.dynamic-fields {
    margin-top: 1.5rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1rem;
}

.field-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 0.85rem;
}

.extra-fields {
    margin-top: 1.5rem;
}

.extra-fields-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.extra-fields-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1rem;
}

.extra-field-row {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr auto;
    gap: 0.5rem;
    align-items: center;
}

.validation {
    color: #e11d48;
    font-size: 0.75rem;
    margin-top: 0.35rem;
}

@keyframes shimmer {
    0% {
        background-position: -200% 0;
    }
    100% {
        background-position: 200% 0;
    }
}

@media (max-width: 768px) {
    .filters-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75rem;
    }

    .table-actions {
        justify-content: flex-start;
    }

    ::v-deep .page-header {
        align-items: center;
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    ::v-deep .page-header .first {
        flex: 1 1 auto;
        min-width: 0;
        white-space: normal;
        word-break: break-word;
        font-size: 1.1rem;
    }

    ::v-deep .page-header > .row {
        margin-left: 0;
        margin-right: 0;
        margin-left: auto;
        flex: 0 0 auto;
    }
}
</style>
